import React, { useState, useEffect } from 'react';
import {
  fetchHeadings,
  calculateMaterials,
} from '../../services/materialCalculatorAPI';
import MaterialResult from './MaterialResult';
import * as XLSX from 'xlsx';
import './MaterialCalculator.css';

const MaterialCalculator = () => {
  const [headings, setHeadings] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedHeading, setSelectedHeading] = useState(null);
  const [quantity, setQuantity] = useState('');
  const [calculatedMaterials, setCalculatedMaterials] = useState([]);
  const [finalTable, setFinalTable] = useState([]);

  useEffect(() => {
    fetchHeadings().then(setHeadings);
  }, []);

  const calculateTotalCost = (materials) =>
    materials.reduce((sum, mat) => {
      if (mat.price_per_unit !== null) {
        return sum + mat.required_quantity * mat.price_per_unit;
      }
      return sum;
    }, 0);

  const handleCalculate = async () => {
    if (!selectedHeading || !quantity) return;
    const res = await calculateMaterials(selectedHeading.id, quantity);
    setCalculatedMaterials(res.materials || []);
  };

  const handleAddToFinalTable = () => {
    if (!selectedHeading || !calculatedMaterials.length) return;

    const isAlreadyAdded = finalTable.some(
      (section) =>
        section.headingName === selectedHeading.name &&
        section.quantity === quantity
    );

    if (isAlreadyAdded) return;

    setFinalTable((prev) => [
      ...prev,
      {
        headingName: selectedHeading.name,
        headingUnit: selectedHeading.unit,
        quantity: quantity,
        materials: calculatedMaterials,
      },
    ]);

    // Reset
    setSearchTerm('');
    setSelectedHeading(null);
    setQuantity('');
    setCalculatedMaterials([]);
  };

  const handleExport = () => {
    const rows = [];

    finalTable.forEach((section) => {
      rows.push({
        'Work Heading': section.headingName,
        'Input Quantity': section.quantity,
        Unit: section.headingUnit,
      });

      section.materials.forEach((material) => {
        rows.push({
          Material: material.material_name,
          Quantity: Math.round(material.required_quantity),
          Unit: material.material_unit,
          'Price/Unit':
            material.price_per_unit !== null
              ? material.price_per_unit
              : 'Not uploaded',
          'Total Price':
            material.price_per_unit !== null
              ? (material.required_quantity * material.price_per_unit).toFixed(2)
              : 'N/A',
        });
      });

      const totalCost = calculateTotalCost(section.materials).toFixed(2);
      rows.push({
        Material: 'Total Heading Cost (₹)',
        'Total Price': totalCost,
      });

      rows.push({}); // Spacer
    });

    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Materials');
    XLSX.writeFile(workbook, 'Material_Summary.xlsx');
  };

  const filteredHeadings = searchTerm
    ? headings.filter((h) =>
        h.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  return (
    <div className="calculator-container">
      <main className="main-content">
        <h2 className="main-title">Material Calculator</h2>

        <div className="input-section">
          <div className="input-group">
            <label>Choose Work Heading:</label>
            <input
              type="text"
              placeholder="Type to search..."
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setSelectedHeading(null);
              }}
            />
            {searchTerm && !selectedHeading && (
              <ul className="search-dropdown">
                {filteredHeadings.map((h) => (
                  <li
                    key={h.id}
                    onClick={() => {
                      setSelectedHeading(h);
                      setSearchTerm(`${h.name} (${h.unit})`);
                    }}
                  >
                    {h.name} ({h.unit})
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="input-group">
            <label>Enter Quantity:</label>
            <input
              type="number"
              value={quantity}
              onChange={(e) => setQuantity(e.target.value)}
              placeholder="Enter quantity"
              min="0"
            />
          </div>

          <div className="button-row">
            <button onClick={handleCalculate} disabled={!selectedHeading || !quantity}>
              Calculate
            </button>
            {calculatedMaterials.length > 0 && (
              <button className="add-btn" onClick={handleAddToFinalTable}>
                ➕ Add to Final Table
              </button>
            )}
          </div>
        </div>

        {calculatedMaterials.length > 0 && (
          <MaterialResult materials={calculatedMaterials} />
        )}

        {finalTable.length > 0 && (
          <section className="summary-section">
            <div className="summary-header">
              <h3>Final Material Summary</h3>
              <button onClick={handleExport}>📤 Export to Excel</button>
            </div>

            {finalTable.map((section, idx) => (
              <div key={idx} className="summary-card">
                <h4>
                  {section.headingName} ({section.quantity} {section.headingUnit}) —{' '}
                  Total Cost: ₹{calculateTotalCost(section.materials).toFixed(2)}
                </h4>
                <table>
                  <thead>
                    <tr>
                      <th>Material</th>
                      <th>Quantity</th>
                      <th>Unit</th>
                      <th>Price/Unit</th>
                      <th>Total Cost</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.materials.map((mat, i) => {
                      const quantity = Math.round(mat.required_quantity);
                      const price = mat.price_per_unit;
                      const total = price ? (price * quantity).toFixed(2) : '—';

                      return (
                        <tr key={i}>
                          <td>{mat.material_name}</td>
                          <td>{quantity}</td>
                          <td>{mat.material_unit}</td>
                          <td>{price !== null ? `₹${price.toFixed(2)}` : 'Not uploaded'}</td>
                          <td>{price !== null ? `₹${total}` : '—'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))}
          </section>
        )}
      </main>
    </div>
  );
};

export default MaterialCalculator;
